import * as React from 'react'

import { Box, Chevron, Text } from '@factorialco/deprecated-design-system'
import moment from 'moment'

import Loading from 'modules/core/components/Loading'
import i18n from 'modules/core/lib/i18n'

import Balance from 'modules/timeoff/components/Leaves/Overview/NewStatsCounter/Balance'
import CycleSelector from 'modules/timeoff/components/Leaves/Overview/NewStatsCounter/CycleSelector'
import TakenDays from 'modules/timeoff/components/Leaves/Overview/NewStatsCounter/Taken'
import TotalDays from 'modules/timeoff/components/Leaves/Overview/NewStatsCounter/Total'
import allowanceStats from 'modules/timeoff/store/collections/stats'
import type Allowance from 'modules/timeoff/store/models/Allowance'
import type { Cycle } from 'modules/timeoff/store/models/AllowanceStat'
import type AllowanceStat from 'modules/timeoff/store/models/AllowanceStat'

interface Props {
  employeeId: number
  allowance: Allowance
  currentStat: AllowanceStat
  currentCycle: Cycle
}

const Counter = ({ employeeId, allowance, currentStat, currentCycle }: Props) => {
  const { current: today } = React.useRef<moment.Moment>(moment())
  const [loading, setLoading] = React.useState(false)
  const [open, setOpen] = React.useState(false)
  const [selectedStat, setStat] = React.useState(currentStat)
  const [selectedCycle, setCycle] = React.useState(currentCycle)
  const nextCycle = currentStat.nextCycle(selectedCycle)

  const onSelect = async (cycleId: string) => {
    setLoading(true)

    const cycle = currentStat.cycles.find((cycle) => cycle.id === cycleId)

    if (cycle && cycleId !== currentCycle.id) {
      const referenceDate = cycle.end.isBefore(today) ? cycle.end : cycle.begin

      const allowanceStat = allowanceStats.findOrInitializeBy(employeeId, allowance, referenceDate)
      await allowanceStat.fetch()
      setStat(allowanceStat)
      setCycle(cycle)
    } else {
      await currentStat.fetch()
      setStat(currentStat)
      setCycle(currentCycle)
    }

    setLoading(false)
  }

  if (loading) return <Loading />

  return (
    <>
      {open && (
        <Box background="grey100" width="full">
          <CycleSelector
            cycles={currentStat.cycles}
            currentCycle={currentCycle}
            selectedCycle={selectedCycle}
            onSelect={onSelect}
          />
          <TotalDays
            allowance={allowance}
            selectedStat={selectedStat}
            selectedCycle={selectedCycle}
          />
          <TakenDays
            allowance={allowance}
            selectedStat={selectedStat}
            selectedCycle={selectedCycle}
            nextCycle={nextCycle}
          />
          <Balance
            selectedStat={selectedStat}
            selectedCycle={selectedCycle}
            nextCycle={nextCycle}
          />
        </Box>
      )}
      <Box
        flexDirection="row"
        gap="s8"
        alignItems="center"
        onClick={() => setOpen(!open)}
        cursor="pointer"
      >
        <Text.Normal color="viridian1200">
          {!open
            ? i18n.t('time_off.new_counter.view_more')
            : i18n.t('time_off.new_counter.view_less')}
        </Text.Normal>
        <Chevron type="brand" direction={open ? 'up' : 'down'} />
      </Box>
    </>
  )
}

export default Counter
